import { useEffect, useRef } from "react";
import { useCardLayout } from "./CardLayoutContext";

type ObservedHeightOptions = {
    /** The element the measurement is taken from; null until mounted. */
    root: HTMLElement | null;
    /** Turns the DOM into a height in pixels, or null when there is none yet. */
    measure: (root: HTMLElement) => number | null;
    /** Receives each new height. Measurement is off when this is undefined. */
    onReport: ((heightPx: number) => void) | undefined;
    /** The elements whose size changes should trigger a new measurement. */
    watch: (root: HTMLElement) => Element[];
    /**
     * Where to listen for nodes being swapped. Watched elements are
     * re-collected whenever children change here, because observers on
     * replaced nodes would otherwise go quiet.
     */
    mutations: (root: HTMLElement) => { target: Node; subtree: boolean };
};

/**
 * Reports a card's height to the layout whenever something that affects it
 * changes, coalesced to one measurement per animation frame. Also measures
 * once more when a drag or resize ends, because a resize paints at whole
 * cells and so can end without any watched element changing size.
 */
export function useObservedHeight({
    root,
    measure,
    onReport,
    watch,
    mutations,
}: ObservedHeightOptions) {
    const subscribeGestureEnd =
        useCardLayout()?.layoutStore.subscribeGestureEnd;
    const latest = useRef({ measure, onReport, watch, mutations });
    latest.current = { measure, onReport, watch, mutations };
    const enabled = onReport !== undefined;

    useEffect(() => {
        if (!root || !enabled) return;

        let frame = 0;
        const report = () => {
            frame = 0;
            const height = latest.current.measure(root);
            if (height !== null) latest.current.onReport?.(height);
        };
        const schedule = () => {
            if (frame === 0) frame = window.requestAnimationFrame(report);
        };

        const sizes = new ResizeObserver(schedule);
        const watchAll = () => {
            sizes.disconnect();
            latest.current
                .watch(root)
                .forEach((element) => sizes.observe(element));
            schedule();
        };
        watchAll();

        const { target, subtree } = latest.current.mutations(root);
        const children = new MutationObserver(watchAll);
        children.observe(target, { childList: true, subtree });

        const unsubscribeGestureEnd = subscribeGestureEnd?.(schedule);

        return () => {
            unsubscribeGestureEnd?.();
            children.disconnect();
            sizes.disconnect();
            window.cancelAnimationFrame(frame);
        };
    }, [root, enabled, subscribeGestureEnd]);
}
